import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQualifications, useDocuments } from '../../hooks';
import { Button, Input, Card, CardHeader, CardBody, Alert, FieldOfWorkField } from '../../components';
import type { QualificationType } from '../../types';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

export const AddQualification: React.FC = () => {
  const navigate = useNavigate();
  const { createQualification, loading, error, clearError } = useQualifications();
  const { uploadDocument } = useDocuments();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    institution: '',
    description: '',
    qualification_type: 'certification' as QualificationType,
    field_of_work: '',
    issue_date: '',
    expiry_date: '',
    credential_id: '',
    credential_url: '',
  });

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const qualificationTypes = [
    { value: 'certification', label: 'Certification' },
    { value: 'license', label: 'License' },
    { value: 'degree', label: 'Degree' },
    { value: 'diploma', label: 'Diploma' },
    { value: 'training', label: 'Training' },
    { value: 'other', label: 'Other' },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileAdd = (file: File) => {
    setFileError(null);
    if (file.size > MAX_FILE_SIZE) { setFileError('File must be under 10MB'); return; }
    if (!ALLOWED_TYPES.includes(file.type) && !file.name.match(/\.(pdf|jpg|jpeg|png|webp)$/i)) {
      setFileError('Allowed: PDF, JPG, PNG, WEBP');
      return;
    }
    setPendingFiles(prev => [...prev, file]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data = { ...formData };
    if (data.credential_url && !/^https?:\/\//i.test(data.credential_url)) {
      data.credential_url = `https://${data.credential_url}`;
    }

    const result = await createQualification(data);
    if (!result.success) return;

    if (pendingFiles.length > 0 && result.id) {
      setUploading(true);
      await Promise.all(
        pendingFiles.map(file =>
          uploadDocument(file, 'certificate', { qualificationId: result.id })
        )
      );
      setUploading(false);
    }

    navigate('/qualifications');
  };

  return (
    <div>
      <Card collapsible={false}>
        <CardHeader>
          <h1 className="text-xl md:text-2xl font-bold">Add Qualification</h1>
        </CardHeader>
        <CardBody>
          {error && (
            <Alert variant="danger" onClose={clearError} className="mb-4">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., First Aid Certification"
              required
            />

            <Input
              label="Institution"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              placeholder="e.g., Red Cross"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Qualification Type
              </label>
              <select
                name="qualification_type"
                value={formData.qualification_type}
                onChange={handleChange}
                className="w-full px-4 py-3.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-h-[48px] text-base md:px-3 md:py-2 md:min-h-0"
                required
              >
                {qualificationTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <FieldOfWorkField
              value={formData.field_of_work}
              onChange={field_of_work => setFormData(prev => ({ ...prev, field_of_work }))}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-h-[100px] text-base md:px-3 md:py-2 md:min-h-0"
                placeholder="Describe the qualification..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Issue Date" name="issue_date" type="date" value={formData.issue_date} onChange={handleChange} />
              <Input label="Expiry Date" name="expiry_date" type="date" value={formData.expiry_date} onChange={handleChange} />
            </div>

            <Input
              label="Credential ID"
              name="credential_id"
              value={formData.credential_id}
              onChange={handleChange}
              placeholder="e.g., CERT-12345"
            />

            <Input
              label="Credential URL"
              name="credential_url"
              type="text"
              placeholder="e.g. facebook.com or https://example.com"
              value={formData.credential_url}
              onChange={handleChange}
            />

            {/* Supporting documents */}
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Supporting Documents</p>
                  <p className="text-xs text-gray-400 mt-0.5">Attach certificates or evidence — optional</p>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add file
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFileAdd(f); e.target.value = ''; }}
                />
              </div>

              {fileError && <p className="text-xs text-red-600">{fileError}</p>}

              {pendingFiles.length > 0 ? (
                <div className="space-y-2">
                  {pendingFiles.map((file, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-700 truncate">{file.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}
                        className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No files selected</p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="submit" loading={loading || uploading} fullWidth className="sm:w-auto">
                {uploading ? 'Uploading documents…' : 'Add Qualification'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate('/qualifications')} fullWidth className="sm:w-auto">
                Cancel
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
};
